import React, { useState } from "react";
import ForgotPassword from "./ForgotPassword";
import Logo from "../assets/favicon.jpeg";
import { Eye, EyeOff } from "lucide-react";
import { getApiBase } from "../utils/apiBase";

type ApiResponse = {
  accessToken?: string;
  refreshToken?: string;
  user?: { id: string; email: string; name?: string; role?: string };
  message?: string;
  msg?: string;
};

type LoginProps = { onSuccess: () => void };

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login: React.FC<LoginProps> = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgot, setShowForgot] = useState(false);

  const API_BASE = getApiBase();

  if (showForgot) return <ForgotPassword />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) { setError("Email and password are required."); return; }
    if (!emailRegex.test(email)) { setError("Please enter a valid email address."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data: ApiResponse = await res.json();
      if (!res.ok) { setError(data.message || data.msg || "Login failed. Please check your credentials."); return; }
     if (!data.accessToken) { setError("No token received from server."); return; }
localStorage.setItem("authToken", data.accessToken);
localStorage.setItem("refreshToken", data.refreshToken || "");
if (data.user) localStorage.setItem("authUser", JSON.stringify(data.user));
onSuccess();
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8f7f5", padding: 16, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      <div style={{ width: 400, maxWidth: "100%" }}>
        {/* Logo + brand */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <img src={Logo} alt="JAC Logo" style={{ width: 64, height: 64, borderRadius: "16px", objectFit: "cover", marginBottom: "14px" }} />
          <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 600, color: "#111827" }}>Scan Pay Go</h1>
          <p style={{ margin: "4px 0 0", fontSize: "14px", color: "#9ca3af" }}>Admin dashboard</p>
        </div>

        {/* Card */}
        <div style={{ background: "#fff", borderRadius: "20px", padding: "32px", border: "1px solid #f3f4f6", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
          <h2 style={{ margin: "0 0 24px", fontSize: "18px", fontWeight: 600, color: "#111827" }}>Sign in</h2>

          {error && (
            <div style={{ marginBottom: "16px", padding: "12px 14px", borderRadius: "10px", background: "#fff5f5", border: "1px solid #fecaca", fontSize: "13px", color: "#b91c1c" }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="username"
                required
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "10px 14px", fontSize: "14px", color: "#111827", outline: "none", boxSizing: "border-box", background: "#fafafa" }}
                onFocus={e => (e.target.style.borderColor = "#F1561D")}
                onBlur={e => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: "10px", padding: "10px 44px 10px 14px", fontSize: "14px", color: "#111827", outline: "none", boxSizing: "border-box", background: "#fafafa" }}
                  onFocus={e => (e.target.style.borderColor = "#F1561D")}
                  onBlur={e => (e.target.style.borderColor = "#e5e7eb")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", display: "flex", alignItems: "center", padding: 0 }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ marginTop: "4px", height: "44px", borderRadius: "10px", border: "none", background: loading ? "#fdba99" : "#F1561D", color: "#fff", fontSize: "15px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", transition: "background 0.15s" }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              style={{ fontSize: "13px", color: "#F1561D", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
